import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, ArrowLeft, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Dices, User, Package, ScrollText, Users, Bookmark, Plus, Brain, Trash2, BookOpen, Eye, EyeOff, Loader2, Library, Anchor, X, Minimize2, Upload, Wand2 } from 'lucide-react';
import { DiceRoller } from '../components/game/DiceRoller';
import { NPCListTab } from '../components/game/NPCListTab';
import { InventoryTab } from '../components/game/InventoryTab';
import { StoryBibleTab } from '../components/game/StoryBibleTab';
import { StoryArcTab } from '../components/game/StoryArcTab';
import { SupportChat } from '../components/SupportChat';
import GalaxyMap, { SYSTEMS } from '../components/game/GalaxyMap';
import WesterosMap, { LOCATIONS as GOT_LOCATIONS } from '../components/game/WesterosMap';
import MiddleEarthMap, { LOCATIONS as LOTR_LOCATIONS } from '../components/game/MiddleEarthMap';
import WarhammerMap, { LOCATIONS as WH40K_LOCATIONS } from '../components/game/WarhammerMap';
import { TimelineBar } from '../components/game/TimelineBar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Label } from '../components/ui/label';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default models list (overridden by backend fetch)
export default function GamePage() {
  const { characterId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  const [character, setCharacter] = useState(null);
  const [allCampaigns, setAllCampaigns] = useState([]);
  const [campaignId, setCampaignId] = useState(location.state?.campaignId || null);
  const [messages, setMessages] = useState([]);
  const [summaries, setSummaries] = useState([]);
  const [campaignState, setCampaignState] = useState(null);  // v2 structured memory doc
  const [v2EditState, setV2EditState] = useState(null);      // working copy for edits
  const [savingV2Section, setSavingV2Section] = useState(null);
  const [expandedV2, setExpandedV2] = useState({ entities: true, events: false, threads: true, player: false });
  const [npcs, setNpcs] = useState([]);
  const [selectedNpc, setSelectedNpc] = useState(null);
  const [memories, setMemories] = useState([]);
  const [newItemName, setNewItemName] = useState('');
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedModel, setSelectedModel] = useState('grok-4-6');
  const [availableModels, setAvailableModels] = useState({ standard_models: [], premium_models: [] });
  
  // Character usage tracking
  const [characterUsage, setCharacterUsage] = useState({ count: 0, limit: 45000, percentage: 0 });
  const [compressing, setCompressing] = useState(false);
  const [messageUsage, setMessageUsage] = useState(null);
  const [storyNotes, setStoryNotes] = useState('');
  const [campaignFacts, setCampaignFacts] = useState('');
  
  // Galaxy map state
  const [characterLocation, setCharacterLocation] = useState(null);
  const [timelinePosition, setTimelinePosition] = useState('');
  const [showMap, setShowMap] = useState(true);
  const [worldCharacters, setWorldCharacters] = useState([]);
  const [chatMinimized, setChatMinimized] = useState(false);
  
  // Collapse sidebars when an interactive map is active (Star Wars / GoT / LOTR / W40K)
  const isStarWars = character?.universe === 'starwars';
  const isGot = character?.universe === 'got';
  const isLotr = character?.universe === 'lotr';
  const isWh40k = character?.universe === 'wh40k';
  const hasMap = isStarWars || isGot || isLotr || isWh40k;
  const defaultTimeline = isGot ? '298 AC' : isLotr ? 'TA 3018' : isWh40k ? '999.M41' : '22 BBY';
  useEffect(() => {
    if (hasMap && showMap) {
      setLeftSidebarOpen(false);
      setRightSidebarOpen(false);
    }
  }, [hasMap]);  const [savingNotes, setSavingNotes] = useState(false);
  const [editableSummary, setEditableSummary] = useState('');
  const [savingSummary, setSavingSummary] = useState(false);
  const [generatingCharPortrait, setGeneratingCharPortrait] = useState(false);
  const [portraitImageModel, setPortraitImageModel] = useState('openai');
  const [portraitEditPrompt, setPortraitEditPrompt] = useState('');
  const [editingPortrait, setEditingPortrait] = useState(false);
  
  // Sidebar states
  const [rightSidebarOpen, setRightSidebarOpen] = useState(() => {
    return typeof window !== 'undefined' && window.innerWidth >= 768;
  });
  const [leftSidebarOpen, setLeftSidebarOpen] = useState(() => {
    return typeof window !== 'undefined' && window.innerWidth >= 1024;
  });
  
  // Dice roller
  const [showDiceRoller, setShowDiceRoller] = useState(false);
  const [diceType, setDiceType] = useState('d20');
  const [diceCount, setDiceCount] = useState(1);
  const [diceModifier, setDiceModifier] = useState(0);
  
  const messagesEndRef = useRef(null);
  const chatScrollRef = useRef(null);
  const [showScrollBottom, setShowScrollBottom] = useState(false);
  const fileInputRef = useRef(null);
  const [uploadingPortrait, setUploadingPortrait] = useState(false);
  const [headerExpanded, setHeaderExpanded] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('gp_header_expanded') === '1';
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('gp_header_expanded', headerExpanded ? '1' : '0');
    }
  }, [headerExpanded]);

  // Helper function to determine model tier - must be defined before use in fetchCharacter
  useEffect(() => {
    const init = async () => {
      await fetchCharacter();
      fetchAllCampaigns();
      fetchUsage();
      // Fetch available models
      try {
        const res = await axios.get(`${API}/ai-models`);
        setAvailableModels(res.data);
      } catch (e) { console.error('Failed to fetch models', e); }
    };
    init();
  }, [characterId]);

  useEffect(() => {
    if (campaignId) {
      fetchMessages();
      fetchSummaries();
      fetchCampaignState();
      fetchNpcs();
      fetchMemories();
      fetchStoryNotes();
      fetchCampaignFacts();
      // Fetch galaxy map data for Star Wars
      fetchWorldMapData();
      // Initialize character usage for this campaign
      axios.get(`${API}/subscription/info`, { withCredentials: true })
        .then(res => {
          const limit = res.data.character_limit || 50000;
          // Find character_count from allCampaigns or use total usage
          const camp = allCampaigns.find(c => c.campaign_id === campaignId);
          const count = camp?.character_count || 0;
          setCharacterUsage({
            count,
            limit,
            percentage: limit > 0 ? Math.round((count / limit) * 100) : 0
          });
        })
        .catch(() => {});
    }
    // No longer auto-creating campaigns - Dashboard handles this
  }, [campaignId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchCharacter = async () => {
    try {
      const response = await axios.get(`${API}/characters`, { withCredentials: true });
      const char = response.data.find(c => c.character_id === characterId);
      setCharacter(char);
      if (char?.gm_rules) setGmRules(char.gm_rules);
      // Set location for galaxy map
      if (char?.starting_location) {
        setCharacterLocation(char.starting_location);
      }
      // Fetch world map data after character loads (needs world_id)
      if (char?.world_id) {
        try {
          const charsRes = await axios.get(`${API}/worlds/${char.world_id}/characters-map`, { withCredentials: true }).catch(() => ({ data: [] }));
          setWorldCharacters(charsRes.data || []);
        } catch {}
      }
    } catch (error) {
      console.error('Failed to fetch character:', error);
      toast.error('Failed to load character');
    }
  };

  const fetchAllCampaigns = async () => {
    try {
      const response = await axios.get(`${API}/campaigns`, { withCredentials: true });
      setAllCampaigns(response.data);
      // Update character usage for current campaign if available
      if (campaignId) {
        const camp = response.data.find(c => c.campaign_id === campaignId);
        if (camp?.character_count !== undefined) {
          setCharacterUsage(prev => ({
            ...prev,
            count: camp.character_count,
            percentage: prev.limit > 0 ? Math.round((camp.character_count / prev.limit) * 100) : 0
          }));
        }
      }
    } catch (error) {
      console.error('Failed to fetch campaigns:', error);
    }
  };

  const createCampaign = async () => {
    try {
      const response = await axios.post(
        `${API}/campaigns?character_id=${characterId}`,
        {},
        { withCredentials: true }
      );
      setCampaignId(response.data.campaign_id);
      fetchAllCampaigns();
    } catch (error) {
      console.error('Failed to create campaign:', error);
      toast.error('Failed to start campaign');
    }
  };

  const [startingChapter, setStartingChapter] = useState(false);
  const [gmRules, setGmRules] = useState('');
  const [savingRules, setSavingRules] = useState(false);

  const saveGmRules = async () => {
    setSavingRules(true);
    try {
      await axios.patch(
        `${API}/characters/${characterId}`,
        { gm_rules: gmRules },
        { withCredentials: true }
      );
      setCharacter(prev => ({ ...prev, gm_rules: gmRules }));
      toast.success('Rules saved! The GM will follow them from now on.');
    } catch (error) {
      toast.error('Failed to save rules');
    } finally {
      setSavingRules(false);
    }
  };

  const startNewChapter = async () => {
    setStartingChapter(true);
    try {
      const response = await axios.post(
        `${API}/campaigns/${characterId}/new-chapter`,
        {},
        { withCredentials: true }
      );
      setCampaignId(response.data.campaign_id);
      toast.success(`Chapter ${response.data.chapter_number} begins!`);
      fetchAllCampaigns();
    } catch (error) {
      console.error('Failed to start new chapter:', error);
      toast.error(error.response?.data?.detail || 'Failed to start new chapter');
    } finally {
      setStartingChapter(false);
    }
  };

  const deleteChapter = async (campId) => {
    if (!window.confirm('Delete this chapter? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/campaigns/${campId}`, { withCredentials: true });
      toast.success('Chapter deleted');
      const remaining = allCampaigns.filter(c => c.campaign_id !== campId && c.character_id === characterId);
      if (remaining.length > 0) {
        setCampaignId(remaining[0].campaign_id);
      }
      fetchAllCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete chapter');
    }
  };

  const handleEditPortrait = async () => {
    if (!portraitEditPrompt.trim()) return;
    setEditingPortrait(true);
    try {
      const payload = {
        prompt: portraitEditPrompt,
        image_model: portraitImageModel,
      };
      if (character.portrait_url && portraitImageModel === 'nano-banana') {
        payload.current_portrait_b64 = character.portrait_url;
      }
      const response = await axios.post(
        `${API}/characters/${characterId}/edit-portrait`,
        payload,
        { withCredentials: true }
      );
      setCharacter(prev => ({ ...prev, portrait_url: response.data.portrait_url }));
      setPortraitEditPrompt('');
      toast.success('Portrait updated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to edit portrait');
    } finally {
      setEditingPortrait(false);
    }
  };


  const toggleNpcActive = async (npcId, currentActive) => {
    try {
      await axios.patch(
        `${API}/npcs/${npcId}`,
        { active: !currentActive },
        { withCredentials: true }
      );
      setNpcs(prev => prev.map(n => n.npc_id === npcId ? { ...n, active: !currentActive } : n));
    } catch (error) {
      toast.error('Failed to toggle NPC');
    }
  };

  const fetchUsage = async () => {
    try {
      const response = await axios.get(`${API}/usage`, { withCredentials: true });
      setMessageUsage(response.data);
    } catch (error) {
      console.error('Failed to fetch usage:', error);
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(
        `${API}/campaigns/${campaignId}/messages`,
        { withCredentials: true }
      );
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const fetchSummaries = async () => {
    try {
      const response = await axios.get(
        `${API}/campaigns/${campaignId}/summaries`,
        { withCredentials: true }
      );
      setSummaries(response.data);
      if (response.data.length > 0) {
        setEditableSummary(response.data.map(s => s.summary).join('\n\n---\n\n'));
      }
    } catch (error) {
      console.error('Failed to fetch summaries:', error);
    }
  };

  const fetchCampaignState = async () => {
    try {
      const res = await axios.get(
        `${API}/campaigns/${campaignId}/campaign-state`,
        { withCredentials: true }
      );
      const doc = res.data?.state || null;
      setCampaignState(doc);
      setV2EditState(doc ? JSON.parse(JSON.stringify(doc)) : null);
    } catch (error) {
      console.error('Failed to fetch campaign-state:', error);
    }
  };

  const saveV2Section = async (sectionKey) => {
    if (!v2EditState) return;
    setSavingV2Section(sectionKey);
    try {
      const payload = { [sectionKey]: v2EditState[sectionKey] };
      await axios.put(
        `${API}/campaigns/${campaignId}/campaign-state`,
        payload,
        { withCredentials: true }
      );
      setCampaignState(prev => ({ ...prev, [sectionKey]: v2EditState[sectionKey] }));
      toast.success(`Saved ${sectionKey.replace('_', ' ')}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSavingV2Section(null);
    }
  };

  const [buildingV2, setBuildingV2] = useState(false);
  const [showV2Panel, setShowV2Panel] = useState(false);
  const buildV2FromV1 = async () => {
    setBuildingV2(true);
    try {
      const resp = await axios.post(
        `${API}/campaigns/${campaignId}/build-v2-from-v1`,
        {},
        { withCredentials: true }
      );
      if (resp.data?.state) {
        setCampaignState(resp.data.state);
        setV2EditState(JSON.parse(JSON.stringify(resp.data.state)));
      }
      setShowV2Panel(true);
      toast.success(`V2 structured view ${resp.data?.mode === 'heavy_migration' ? 'built' : 'refreshed'}`);
    } catch (e) {
      toast.error('V2 build failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setBuildingV2(false);
    }
  };

  const fetchStoryNotes = async () => {
    try {
      const response = await axios.get(
        `${API}/campaigns/${campaignId}/story-notes`,
        { withCredentials: true }
      );
      setStoryNotes(response.data.notes || '');
    } catch (error) {
      console.error('Failed to fetch story notes:', error);
    }
  };

  const saveStoryNotes = async () => {
    setSavingNotes(true);
    try {
      await axios.put(
        `${API}/campaigns/${campaignId}/story-notes`,
        { notes: storyNotes },
        { withCredentials: true }
      );
      toast.success('Story notes saved');
    } catch (error) {
      toast.error('Failed to save notes');
    } finally {
      setSavingNotes(false);
    }
  };

  const fetchCampaignFacts = async () => {
    try {
      const response = await axios.get(
        `${API}/campaigns/${campaignId}/facts`,
        { withCredentials: true }
      );
      setCampaignFacts(response.data.facts || '');
    } catch (error) {
      // Facts may not exist yet — that's fine
    }
  };

  const fetchWorldMapData = async () => {
    if (!character?.world_id) return;
    try {
      const charsRes = await axios.get(`${API}/worlds/${character.world_id}/characters-map`, { withCredentials: true }).catch(() => ({ data: [] }));
      setWorldCharacters(charsRes.data || []);
    } catch {}
  };


  const saveCampaignSummary = async () => {
    setSavingSummary(true);
    try {
      await axios.put(
        `${API}/campaigns/${campaignId}/campaign-summary`,
        { summary: editableSummary },
        { withCredentials: true }
      );
      toast.success('Campaign summary saved');
      fetchSummaries();
    } catch (error) {
      toast.error('Failed to save summary');
    } finally {
      setSavingSummary(false);
    }
  };

  const deletePreviouslyOn = async () => {
    try {
      await axios.delete(`${API}/campaigns/${campaignId}/previously-on`, { withCredentials: true });
      toast.success('Previously On deleted');
      // Refresh campaign data
      fetchAllCampaigns();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const deleteSummaries = async () => {
    try {
      await axios.delete(`${API}/campaigns/${campaignId}/summaries`, { withCredentials: true });
      setEditableSummary('');
      setSummaries([]);
      toast.success('All summaries deleted');
    } catch (error) {
      toast.error('Failed to delete summaries');
    }
  };

  const anchorHere = async (messageId) => {
    if (!window.confirm('Set this as the new starting point? All messages before this will be deleted and summaries cleared.')) return;
    try {
      const resp = await axios.post(
        `${API}/campaigns/${campaignId}/anchor-here`,
        { message_id: messageId },
        { withCredentials: true }
      );
      toast.success(`Anchored! Deleted ${resp.data.deleted_count} old messages.`);
      fetchMessages();
      fetchSummaries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to anchor');
    }
  };


  const fetchNpcs = async () => {
    try {
      const response = await axios.get(
        `${API}/campaigns/${campaignId}/npcs`,
        { withCredentials: true }
      );
      setNpcs(response.data);
    } catch (error) {
      console.error('Failed to fetch NPCs:', error);
    }
  };

  const updateNpc = async (npcId, updates) => {
    try {
      await axios.patch(
        `${API}/npcs/${npcId}`,
        updates,
        { withCredentials: true }
      );
      fetchNpcs();
      toast.success('NPC updated');
    } catch (error) {
      toast.error('Failed to update NPC');
    }
  };

  const deleteNpc = async (npcId) => {
    if (window.confirm('Delete this character card?')) {
      try {
        await axios.delete(`${API}/npcs/${npcId}`, { withCredentials: true });
        setNpcs(npcs.filter(n => n.npc_id !== npcId));
        setSelectedNpc(null);
        toast.success('Character card deleted');
      } catch (error) {
        toast.error('Failed to delete NPC');
      }
    }
  };

  const fetchMemories = async () => {
    try {
      const response = await axios.get(`${API}/campaigns/${campaignId}/memories`, { withCredentials: true });
      setMemories(response.data);
    } catch (error) {
      console.error('Failed to fetch memories:', error);
    }
  };

  const rememberMessage = async (messageId) => {
    try {
      await axios.post(`${API}/messages/${messageId}/remember`, {}, { withCredentials: true });
      toast.success('Message saved to memories');
      fetchMemories();
    } catch (error) {
      toast.error('Failed to remember message');
    }
  };

  const deleteMemory = async (memoryId) => {
    try {
      await axios.delete(`${API}/memories/${memoryId}`, { withCredentials: true });
      setMemories(memories.filter(m => m.memory_id !== memoryId));
      toast.success('Memory removed');
    } catch (error) {
      toast.error('Failed to delete memory');
    }
  };

  const deleteMessage = async (messageId) => {
    try {
      await axios.delete(`${API}/messages/${messageId}`, { withCredentials: true });
      setMessages(msgs => msgs.filter(m => m.message_id !== messageId));
      toast.success('Message deleted');
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  const addInventoryItem = () => {
    if (!newItemName.trim()) return;
    const updated = [...(character.inventory || []), newItemName.trim()];
    updateCharacterStats({ inventory: updated });
    setNewItemName('');
    toast.success(`Added "${newItemName.trim()}" to inventory`);
  };

  const [confirmCompress, setConfirmCompress] = useState(false);
  const compressSummaries = async () => {
    // Inline two-click confirmation (window.confirm is suppressed in iframe/preview)
    if (!confirmCompress) {
      setConfirmCompress(true);
      toast.info('Click "Confirm Compress" to fold old messages into living memory. Recent messages are kept.', { duration: 5000 });
      setTimeout(() => setConfirmCompress((c) => (c ? false : c)), 5000);
      return;
    }
    setConfirmCompress(false);

    setCompressing(true);
    try {
      // Start the compression job
      const startResponse = await axios.post(
        `${API}/campaigns/${campaignId}/compress-summaries`,
        {},
        { withCredentials: true }
      );
      
      const jobId = startResponse.data.job_id;
      if (!jobId) {
        toast.error('Failed to start compression');
        setCompressing(false);
        return;
      }
      
      toast('Compressing story with Claude Opus... This may take a minute or two.', { duration: 10000 });
      
      // Poll for completion — no timeout, checks every 5 seconds
      const poll = async () => {
        try {
          const statusRes = await axios.get(
            `${API}/campaigns/${campaignId}/compress-status`,
            { withCredentials: true }
          );
          
          const { status } = statusRes.data;
          
          if (status === 'done') {
            const memSize = statusRes.data.memory_size ? `Memory: ${(statusRes.data.memory_size / 1000).toFixed(1)}k / ${(statusRes.data.memory_cap / 1000).toFixed(0)}k` : '';
            toast.success(`Story compressed! Saved ${(statusRes.data.saved_characters || 0).toLocaleString()} characters. ${memSize}`, { duration: 8000 });
            fetchSummaries();
            fetchCampaignState();
            fetchMessages();
            
            if (statusRes.data.character_count !== undefined) {
              setCharacterUsage({
                count: statusRes.data.character_count,
                limit: statusRes.data.character_limit,
                percentage: Math.round((statusRes.data.character_count / statusRes.data.character_limit) * 100)
              });
            }
            setCompressing(false);
            return;
          }
          
          if (status === 'failed') {
            toast.error(statusRes.data.error || 'Compression failed', { duration: 10000 });
            setCompressing(false);
            return;
          }
          
          // Still running — poll again in 5 seconds
          setTimeout(poll, 5000);
        } catch (pollError) {
          // Network hiccup during poll — retry, don't give up
          setTimeout(poll, 8000);
        }
      };
      
      // Start polling after 3 seconds (give Claude a head start)
      setTimeout(poll, 3000);
      
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || 'Failed to start compression', { duration: 8000 });
      setCompressing(false);
    }
  };
  const handleSendMessage = async (e, withDice = false) => {
    e.preventDefault();
    
    if (!campaignId) return;
    if (!withDice && !inputMessage.trim()) return;

    const userMessage = inputMessage.trim() || (withDice ? '*rolls dice*' : '');
    setInputMessage('');
    setLoading(true);

    let diceRoll = null;
    if (withDice) {
      const diceMax = parseInt(diceType.replace('d', ''));
      const count = Math.max(1, Math.min(diceCount, 100));
      const rolls = Array.from({ length: count }, () => Math.floor(Math.random() * diceMax) + 1);
      const rollSum = rolls.reduce((a, b) => a + b, 0);
      const total = rollSum + diceModifier;
      
      diceRoll = {
        type: diceType,
        count: count,
        modifier: diceModifier,
        rolls: rolls,
        roll: rollSum,
        result: total
      };
      
      const rollsStr = count > 1 ? ` (${rolls.join(', ')})` : '';
      toast.success(`Rolled ${count}${diceType}: ${rollSum}${rollsStr} + ${diceModifier} = ${total}`, { duration: 4000 });
    }

    const tempUserMsg = {
      message_id: `temp_${Date.now()}`,
      role: 'user',
      content: userMessage + (diceRoll ? `\n[${diceRoll.count}${diceRoll.type}${diceRoll.modifier ? '+' + diceRoll.modifier : ''}: ${diceRoll.count > 1 ? '(' + diceRoll.rolls.join('+') + ')' : diceRoll.roll}${diceRoll.modifier ? ' + ' + diceRoll.modifier : ''} = ${diceRoll.result}]` : ''),
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempUserMsg]);

    try {
      // Use the SSE streaming endpoint so the ingress doesn't kill slow
      // models (Euryale 70B, Cydonia 24B, long Opus turns). The stream
      // emits ": keepalive" comments every ~10s while the LLM is
      // generating, then a final `event: done` with the full JSON
      // payload. Bytes flow constantly → ingress proxy_read_timeout
      // never fires.
      const requestStartMs = Date.now();
      const controller = new AbortController();
      const resp = await fetch(`${API}/chat/stream`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          campaign_id: campaignId,
          message: userMessage,
          model_id: selectedModel,
          dice_roll: diceRoll,
        }),
        signal: controller.signal,
      });

      if (!resp.ok) {
        // HTTP-level failure (auth, 4xx, etc.) — parse and throw axios-shaped error
        let detail = null;
        try {
          const j = await resp.json();
          detail = j?.detail;
        } catch (_) { /* body not JSON */ }
        const err = new Error(detail || `HTTP ${resp.status}`);
        err.response = { status: resp.status, data: { detail } };
        throw err;
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let doneEvent = null;
      let errorEvent = null;
      let streamBroken = false; // Set true if the connection drops mid-stream.

      // Parse SSE stream. Events are separated by "\n\n"; each event
      // has "event: NAME" and/or "data: JSON" lines.
      try {
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          let idx;
          while ((idx = buffer.indexOf('\n\n')) !== -1) {
            const rawEvent = buffer.slice(0, idx);
            buffer = buffer.slice(idx + 2);
            if (!rawEvent.trim() || rawEvent.startsWith(':')) continue; // keepalive comment

            let evtName = 'message';
            const dataLines = [];
            for (const line of rawEvent.split('\n')) {
              if (line.startsWith('event:')) evtName = line.slice(6).trim();
              else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
            }
            const dataStr = dataLines.join('\n');
            if (!dataStr) continue;

            if (evtName === 'done') {
              try { doneEvent = JSON.parse(dataStr); } catch (_) { /* ignore */ }
            } else if (evtName === 'error') {
              try { errorEvent = JSON.parse(dataStr); } catch (_) { errorEvent = { detail: dataStr }; }
            }
          }
        }
      } catch (readErr) {
        // Network drop mid-stream (shitty wifi, mobile handoff, etc.).
        // The backend most likely finished the LLM call and stored the
        // message in the DB before we lost the connection — try to
        // recover by polling the messages endpoint below.
        console.warn('Chat stream read failed mid-flight:', readErr?.message || readErr);
        streamBroken = true;
      }

      if (errorEvent) {
        const err = new Error(errorEvent.detail || 'AI request failed');
        err.response = { status: errorEvent.status || 500, data: { detail: errorEvent.detail } };
        throw err;
      }

      // ─── Poll-recovery for network drops ──────────────────────────
      // If we never got a `done` event (either the stream ended cleanly
      // without one, OR the connection died mid-flight), poll the
      // messages endpoint up to 4 times over ~6 seconds. The backend
      // writes the AI reply to Mongo before the stream closes, so a
      // freshly-saved assistant message with a timestamp >= our request
      // start is proof the turn completed on the server. Silently
      // recover from network flakiness this way.
      if (!doneEvent) {
        const requestStartTs = new Date(requestStartMs - 1000); // 1s of clock skew slack
        for (let attempt = 0; attempt < 4; attempt++) {
          await new Promise(r => setTimeout(r, 1500));
          try {
            const pollResp = await axios.get(
              `${API}/campaigns/${campaignId}/messages`,
              { withCredentials: true, timeout: 8000 }
            );
            const msgs = pollResp.data || [];
            const recentAI = [...msgs].reverse().find(
              m => m.role === 'assistant' && new Date(m.timestamp) >= requestStartTs
            );
            if (recentAI) {
              // Rebuild the same shape as the `done` event so the rest
              // of the code path is identical.
              doneEvent = {
                message_id: recentAI.message_id,
                content: recentAI.content,
                model_id: selectedModel,
                model_tier: null,
                timestamp: recentAI.timestamp,
                character_count: undefined,
                character_limit: undefined,
                auto_compressed: false,
                current_location: null,
                current_in_game_time: null,
              };
              toast.success(
                streamBroken
                  ? 'Network dropped mid-stream — recovered the reply from the server.'
                  : 'Stream ended abruptly — recovered the reply from the server.',
                { duration: 4000 }
              );
              break;
            }
          } catch (_) { /* keep polling */ }
        }
      }

      if (!doneEvent) {
        const err = new Error('Stream ended without a final response.');
        err.response = { status: 502, data: { detail: 'Stream ended unexpectedly' } };
        throw err;
      }

      // Emulate the old axios shape so downstream code needs no changes.
      const response = { data: doneEvent };

      const aiMsg = {
        message_id: response.data.message_id,
        role: 'assistant',
        content: response.data.content,
        timestamp: response.data.timestamp
      };
      setMessages(prev => [...prev, aiMsg]);
      setLoading(false);

      // Update character usage
      if (response.data.character_count !== undefined) {
        setCharacterUsage({
          count: response.data.character_count,
          limit: response.data.character_limit,
          percentage: Math.round((response.data.character_count / response.data.character_limit) * 100)
        });
      }

      fetchUsage();

      // Update galaxy map location from AI response
      if (response.data.current_location) {
        setCharacterLocation(response.data.current_location);
      }
      if (response.data.current_in_game_time) {
        setTimelinePosition(response.data.current_in_game_time);
      }

      // Refresh data after background tasks complete (stats, inventory, NPC cards, facts)
      setTimeout(fetchNpcs, 3000);
      setTimeout(fetchCharacter, 5000);
      setTimeout(fetchCampaignFacts, 5000);
      setTimeout(() => { fetchCharacter(); fetchNpcs(); fetchWorldMapData(); }, 12000);

    } catch (error) {
      console.error('Failed to send message:', error);
      const status = error.response?.status;
      const detail = error.response?.data?.detail;

      if (status === 401 || error.isSessionExpired) {
        toast.error('Your session expired. Please log in again.', { duration: 5000 });
        navigate('/');
      } else if (status === 429) {
        // Character limit reached — prominent warning
        const detailMsg = detail || 'Character limit reached. Please compress your story.';
        toast.error(detailMsg, { duration: 15000 });
        setCharacterUsage(prev => ({ ...prev, percentage: 100 }));
      } else if (status === 524 || status === 504 || status === 522) {
        // Cloudflare/K8s upstream timeout — Opus 4.7 took >100s edge limit.
        // Backend is fine, the AI just took too long.
        toast.error(
          'The AI took too long to respond (>100s edge timeout). Try a shorter prompt, or switch to Claude Sonnet 4.6 / Gemini for faster turnaround.',
          { duration: 10000 }
        );
      } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error('Request timed out — the AI took too long. Try a shorter message or different model.', { duration: 6000 });
      } else if (!error.response) {
        toast.error('Network error — check your connection and try again.', { duration: 5000 });
      } else if (status >= 500) {
        // Generic 5xx with a body
        if (detail) {
          toast.error(`AI service error: ${detail}`, { duration: 8000 });
        } else {
          // Body was stripped (most likely an upstream timeout that isn't 524).
          // Tell the user it might just be slow and to try again.
          toast.error(
            `AI service hiccup (HTTP ${status}). Likely a slow response from the model — try again, or switch to a faster model like Claude Sonnet.`,
            { duration: 8000 }
          );
        }
      } else {
        toast.error(detail || `Failed to send message (HTTP ${status})`);
      }
      setMessages(prev => prev.filter(m => m.message_id !== tempUserMsg.message_id));
      setLoading(false);
    }
  };

  const updateCharacterStats = async (updates) => {
    try {
      await axios.patch(
        `${API}/characters/${characterId}`,
        updates,
        { withCredentials: true }
      );
      fetchCharacter();
      toast.success('Character updated');
    } catch (error) {
      toast.error('Failed to update character');
    }
  };

  if (!character) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const characterCampaigns = allCampaigns.filter(c => c.character_id === characterId);
  const currentCampaign = characterCampaigns.find(c => c.campaign_id === campaignId);

  return (
    <div className="h-screen h-[100dvh] flex flex-col overflow-hidden relative" data-testid="game-page">
      {/* Map Background — Star Wars Galaxy or Westeros depending on universe */}
      {isStarWars && showMap && (
        <div className="absolute inset-0 z-0" data-testid="galaxy-map-bg">
          <GalaxyMap
            characterLocation={characterLocation}
            onSystemClick={(sys) => toast(`${sys.name} — ${sys.region}`, { duration: 2000 })}
            worldCharacters={worldCharacters}
            events={[]}
            timelinePosition={timelinePosition || character?.timeline_position || '22 BBY'}
          />
        </div>
      )}
      {isGot && showMap && (
        <div className="absolute inset-0 z-0" data-testid="westeros-map-bg">
          <WesterosMap
            characterLocation={characterLocation}
            onLocationClick={(loc) => toast(`${loc.name} — ${loc.region}`, { duration: 2000 })}
            worldCharacters={worldCharacters}
            events={[]}
            timelinePosition={timelinePosition || character?.timeline_position || '298 AC'}
          />
        </div>
      )}
      {isLotr && showMap && (
        <div className="absolute inset-0 z-0" data-testid="middle-earth-map-bg">
          <MiddleEarthMap
            characterLocation={characterLocation}
            onLocationClick={(loc) => toast(`${loc.name} — ${loc.region}`, { duration: 2000 })}
            worldCharacters={worldCharacters}
            events={[]}
            timelinePosition={timelinePosition || character?.timeline_position || 'TA 3018'}
          />
        </div>
      )}
      {isWh40k && showMap && (
        <div className="absolute inset-0 z-0" data-testid="warhammer-map-bg">
          <WarhammerMap
            characterLocation={characterLocation}
            onLocationClick={(loc) => toast(`${loc.name} — ${loc.region}`, { duration: 2000 })}
            worldCharacters={worldCharacters}
            events={[]}
            timelinePosition={timelinePosition || character?.timeline_position || '999.M41'}
          />
        </div>
      )}
      
      {/* Header — semi-transparent over map. Collapsible to a single row. */}
      <div className={`${hasMap && showMap ? 'bg-slate-950/80 backdrop-blur-xl' : 'glass-card'} border-b border-white/5 px-3 sm:px-6 py-2 flex-shrink-0 sticky top-0 z-40`}>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/dashboard')}
              className="text-slate-400 hover:text-blue-400 h-8 w-8 flex-shrink-0"
              data-testid="back-to-dashboard-btn"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>

            <button
              onClick={() => setHeaderExpanded(!headerExpanded)}
              className="text-slate-500 hover:text-blue-400 transition-colors flex-shrink-0 h-7 w-7 flex items-center justify-center rounded hover:bg-white/5"
              data-testid="toggle-header-expand"
              title={headerExpanded ? 'Collapse header' : 'Expand header'}
            >
              {headerExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>

            <h1 className="text-sm sm:text-base font-bold text-blue-400 font-mono truncate" data-testid="header-character-name">
              {character.name}
            </h1>

            {/* Compact inline meta — visible always on sm+ */}
            <div className="hidden sm:flex items-center gap-1.5 text-xs text-slate-400 min-w-0">
              <span className="text-blue-400/80 truncate">{character.race}</span>
              <span className="text-slate-600">·</span>
              <span className="text-purple-400/80 truncate">{character.char_class}</span>
              <span className="text-slate-600">·</span>
              {character.ruleset === 'gurps' ? (
                <span className="text-blue-400/80">{character.stats.total_points || 100} CP</span>
              ) : (
                <span className="text-blue-400/80">L{character.stats.level}</span>
              )}
            </div>

            {/* Compact usage pill — visible always when usage data present */}
            {characterUsage.limit > 0 && (
              <div className={`hidden md:flex items-center gap-1.5 text-[11px] font-mono px-2 py-0.5 rounded-full border ${
                characterUsage.percentage >= 90 ? 'border-red-500/40 text-red-300 bg-red-500/10' :
                characterUsage.percentage >= 75 ? 'border-yellow-500/40 text-yellow-300 bg-yellow-500/10' :
                'border-white/10 text-slate-400 bg-slate-900/40'
              }`} data-testid="header-usage-pill">
                {characterUsage.percentage}%
              </div>
            )}
          </div>

          <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
            {/* New Chapter Button */}
            {characterCampaigns.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={startNewChapter}
                disabled={startingChapter}
                className="border-purple-400/30 text-purple-400 hover:bg-purple-400/10 text-xs h-7 px-2"
                data-testid="new-chapter-btn"
              >
                {startingChapter ? <Loader2 className="h-3 w-3 animate-spin sm:mr-1" /> : <BookOpen className="h-3 w-3 sm:mr-1" />}
                <span className="hidden sm:inline">{startingChapter ? 'Creating...' : 'New Chapter'}</span>
              </Button>
            )}

            {/* Chapter Selector */}
            {characterCampaigns.length > 1 && (
              <div className="hidden md:flex items-center gap-1">
                <Select value={campaignId || ''} onValueChange={(val) => setCampaignId(val)}>
                  <SelectTrigger className="w-32 bg-slate-950/50 border-white/10 text-slate-200 text-xs h-7" data-testid="chapter-selector">
                    <SelectValue placeholder="Chapter" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900">
                    {characterCampaigns
                      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                      .map((camp) => (
                        <SelectItem key={camp.campaign_id} value={camp.campaign_id} className="text-slate-200 text-xs">
                          Ch. {camp.chapter_number || 1} — {new Date(camp.created_at).toLocaleDateString()}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => deleteChapter(campaignId)}
                  className="h-7 w-7 p-0 text-red-400/60 hover:text-red-400 hover:bg-red-400/10"
                  data-testid="delete-chapter-btn"
                  title="Delete current chapter"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}

            {characterUsage.percentage >= 90 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/settings')}
                className="border-blue-400/30 text-blue-400 hover:bg-blue-400/10 text-xs h-7 px-2"
              >
                Upgrade
              </Button>
            )}

            <SupportChat />

            {hasMap && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowMap(!showMap)}
                className={`text-xs font-mono h-7 px-2 ${showMap ? 'text-blue-400' : 'text-slate-500'}`}
                data-testid="toggle-map"
              >
                {showMap ? 'Hide Map' : (isGot ? 'Westeros' : isLotr ? 'M-earth' : isWh40k ? 'Segmentum' : 'Galaxy')}
              </Button>
            )}
          </div>
        </div>

        {/* Expanded section — full details */}
        {headerExpanded && (
          <div className="mt-2 pt-2 border-t border-white/5 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs" data-testid="header-expanded">
            {/* Ruleset + race/class on mobile */}
            <div className="flex items-center gap-2 flex-wrap sm:hidden">
              <span className="text-blue-400">{character.race}</span>
              <span className="text-slate-500">·</span>
              <span className="text-purple-400">{character.char_class}</span>
              <span className="text-slate-500">·</span>
              {character.ruleset === 'gurps' ? (
                <span className="text-blue-400">{character.stats.total_points || 100} CP</span>
              ) : (
                <span className="text-blue-400">Level {character.stats.level}</span>
              )}
              {character.ruleset && character.ruleset !== 'freeform' && (
                <span className="text-amber-400/70 font-mono text-[10px] px-1.5 py-0.5 bg-amber-500/10 rounded border border-amber-500/15">
                  {character.ruleset === 'dnd5e' ? 'D&D 5e' : character.ruleset === 'pathfinder2e' ? 'PF2e' : character.ruleset === 'starfinder' ? 'Starfinder' : character.ruleset === 'gurps' ? 'GURPS' : character.ruleset}
                </span>
              )}
            </div>
            <div className="hidden sm:flex items-center gap-2">
              {character.ruleset && character.ruleset !== 'freeform' && (
                <span className="text-amber-400/70 font-mono text-[10px] px-1.5 py-0.5 bg-amber-500/10 rounded border border-amber-500/15">
                  {character.ruleset === 'dnd5e' ? 'D&D 5e' : character.ruleset === 'pathfinder2e' ? 'PF2e' : character.ruleset === 'starfinder' ? 'Starfinder' : character.ruleset === 'gurps' ? 'GURPS' : character.ruleset}
                </span>
              )}
            </div>

            {/* Character Usage Display */}
            {characterUsage.limit > 0 && (
              <div>
                <div className="text-[11px] text-slate-400">
                  {characterUsage.count.toLocaleString()} / {characterUsage.limit.toLocaleString()} chars ({characterUsage.percentage}%)
                </div>
                <div className="w-full h-1.5 bg-slate-800 rounded-full mt-1 max-w-xs">
                  <div
                    className={`h-full rounded-full transition-all ${
                      characterUsage.percentage >= 90 ? 'bg-red-500' :
                      characterUsage.percentage >= 75 ? 'bg-yellow-500' :
                      'bg-blue-500'
                    }`}
                    style={{ width: `${Math.min(characterUsage.percentage, 100)}%` }}
                  />
                </div>
              </div>
            )}

            {/* Message Usage Display */}
            {messageUsage && (
              <div className="text-[11px] text-slate-500" data-testid="message-usage">
                {messageUsage.tier === 'free' ? (
                  <span>{messageUsage.free_messages_limit - messageUsage.free_messages_used} free msgs left</span>
                ) : messageUsage.premium_messages_limit > 0 ? (
                  <span>{messageUsage.premium_messages_limit - messageUsage.premium_messages_used} premium msgs left this month</span>
                ) : (
                  <span className="text-slate-600">{messageUsage.tier_name}</span>
                )}
                {messageUsage.matrix_codes > 0 && (
                  <span className="ml-2 text-emerald-400 font-mono">{messageUsage.matrix_codes} Matrix Codes</span>
                )}
              </div>
            )}
          </div>
        )}

      </div>

      {/* Timeline Bar removed — top status bar retired per user request. */}

      <div className={`flex-1 flex overflow-hidden ${hasMap && showMap ? 'relative pointer-events-none' : ''}`}>

        {/* Toggle Left Sidebar */}
        <button
          onClick={() => setLeftSidebarOpen(!leftSidebarOpen)}
          className="w-12 md:w-8 lg:w-6 bg-slate-900/70 hover:bg-slate-900 border-r border-white/10 flex items-center justify-center transition-colors flex-shrink-0 pointer-events-auto"
          data-testid="toggle-left-sidebar"
        >
          {leftSidebarOpen ? <ChevronLeft className="h-5 w-5 md:h-4 md:w-4 text-slate-300" /> : <ChevronRight className="h-5 w-5 md:h-4 md:w-4 text-slate-300" />}
        </button>

        {/* Left Sidebar — Character Sheet / NPCs / Inventory */}
        <AnimatePresence>
          {leftSidebarOpen && (
            <>
              <div className="fixed inset-0 bg-black/60 z-[55] lg:hidden" onClick={() => setLeftSidebarOpen(false)} />
              <motion.div
                initial={{ x: '-100%', opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: '-100%', opacity: 0 }}
                transition={{ type: 'tween', duration: 0.2 }}
                className={`pointer-events-auto border-r border-white/5 overflow-hidden flex flex-col h-[100dvh] lg:h-full fixed lg:relative left-0 top-0 z-[60] lg:z-auto w-full lg:w-[320px] ${hasMap && showMap ? 'bg-slate-950 lg:bg-slate-950/90 lg:backdrop-blur-xl' : 'bg-slate-950 lg:bg-transparent glass-card'}`}
                data-testid="left-sidebar"
              >
                <Tabs defaultValue="sheet" className="h-full flex flex-col">
                  <div className="flex items-center bg-slate-950 lg:bg-slate-900/50 flex-shrink-0 sticky top-0 z-20 pt-[env(safe-area-inset-top)] border-b border-white/5 lg:border-b-0">
                    <button
                      onClick={() => setLeftSidebarOpen(false)}
                      className="lg:hidden flex items-center justify-center w-12 h-12 text-slate-300 hover:text-white hover:bg-slate-700/50 transition-colors flex-shrink-0"
                      data-testid="left-sidebar-close-btn"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    <TabsList className="grid flex-1 grid-cols-3 bg-transparent">
                      <TabsTrigger value="sheet" className="data-[state=active]:bg-blue-400/20 text-xs">
                        <User className="h-4 w-4 mr-1" /><span className="hidden sm:inline">Sheet</span>
                      </TabsTrigger>
                      <TabsTrigger value="npcs" className="data-[state=active]:bg-blue-400/20 text-xs">
                        <Users className="h-4 w-4 mr-1" /><span className="hidden sm:inline">NPCs</span>
                      </TabsTrigger>
                      <TabsTrigger value="inventory" className="data-[state=active]:bg-blue-400/20 text-xs">
                        <Package className="h-4 w-4 mr-1" /><span className="hidden sm:inline">Items</span>
                      </TabsTrigger>
                    </TabsList>
                  </div>

                  {/* Character Summary */}
                  <div className="flex-shrink-0 px-4 py-2 border-b border-white/5 bg-slate-950/80">
                    <div className="flex items-center justify-between text-xs font-mono">
                      <span className="text-blue-400 font-bold truncate">{character.name}</span>
                      <div className="flex gap-3 text-slate-400">
                        <span>HP <span className={character.stats.hp <= (character.stats.max_hp || 10) / 4 ? 'text-red-400' : 'text-blue-300'}>{character.stats.hp}/{character.stats.max_hp}</span></span>
                        {character.ruleset === 'gurps' ? (
                          <>
                            <span>DR <span className="text-blue-300">{character.stats.damage_resistance || 0}</span></span>
                            <span><span className="text-amber-400">{character.stats.total_points || 100} CP</span></span>
                          </>
                        ) : character.ruleset === 'starfinder' ? (
                          <>
                            <span>EAC <span className="text-blue-300">{character.stats.eac || 10}</span></span>
                            <span>Lv <span className="text-blue-300">{character.stats.level}</span></span>
                          </>
                        ) : (
                          <>
                            <span>AC <span className="text-blue-300">{character.stats.armor_class}</span></span>
                            <span>Lv <span className="text-blue-300">{character.stats.level}</span></span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Character Sheet (left sidebar) */}
                <TabsContent value="sheet" className="flex-1 min-h-0 overflow-y-auto p-4">
                    <div className="space-y-4 text-xs">
                      <h3 className="text-lg font-bold text-blue-400 font-mono">Character Sheet</h3>
                      
                      {/* Portrait Section */}
                      <div className="space-y-2" data-testid="portrait-section">
                        {character.portrait_url ? (
                          <div className="relative group">
                            <img
                              src={character.portrait_url}
                              alt={character.name}
                              className="w-full max-h-[28rem] object-contain rounded-lg border border-white/10 bg-slate-950/60"
                              data-testid="character-portrait"
                            />
                          </div>
                        ) : (
                          <div className="w-full h-32 rounded-lg border border-dashed border-white/20 flex items-center justify-center text-slate-500 text-xs">
                            No portrait yet
                          </div>
                        )}
                        
                        {/* Image Generator Dropdown */}
                        <div className="flex items-center gap-1.5">
                          <select
                            value={portraitImageModel}
                            onChange={(e) => setPortraitImageModel(e.target.value)}
                            className="flex-1 bg-slate-950/50 border border-white/10 rounded px-2 py-1 text-slate-200 text-xs"
                            data-testid="portrait-model-select"
                          >
                            <option value="openai">OpenAI (GPT Image)</option>
                            <option value="nano-banana">Nano Banana (Gemini)</option>
                            <option value="grok">Grok Imagine (xAI)</option>
                            <option value="leonardo">Leonardo AI</option>
                          </select>
                          <Button
                            size="sm"
                            onClick={async () => {
                              setGeneratingCharPortrait(true);
                              try {
                                const res = await axios.post(`${API}/characters/${characterId}/generate-portrait`, { image_model: portraitImageModel }, { withCredentials: true });
                                setCharacter(prev => ({ ...prev, portrait_url: res.data.portrait_url }));
                                toast.success('Portrait generated!');
                              } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                              finally { setGeneratingCharPortrait(false); }
                            }}
                            disabled={generatingCharPortrait}
                            className="bg-blue-600/80 hover:bg-blue-500 text-white text-xs h-7 px-2"
                            data-testid="generate-portrait-btn"
                          >
                            {generatingCharPortrait ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Generate'}
                          </Button>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
                            className="hidden"
                            data-testid="portrait-upload-input"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              if (file.size > 5 * 1024 * 1024) {
                                toast.error('Image too large (max 5 MB)');
                                e.target.value = '';
                                return;
                              }
                              setUploadingPortrait(true);
                              try {
                                const fd = new FormData();
                                fd.append('file', file);
                                const res = await axios.post(
                                  `${API}/characters/${characterId}/upload-portrait`,
                                  fd,
                                  { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
                                );
                                setCharacter(prev => ({ ...prev, portrait_url: res.data.portrait_url }));
                                toast.success('Portrait uploaded!');
                              } catch (err) {
                                toast.error(err.response?.data?.detail || 'Upload failed');
                              } finally {
                                setUploadingPortrait(false);
                                e.target.value = '';
                              }
                            }}
                          />
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploadingPortrait}
                            className="border-emerald-400/30 text-emerald-400 hover:bg-emerald-400/10 text-xs h-7 px-2"
                            data-testid="upload-portrait-btn"
                            title="Upload your own image (max 5 MB)"
                          >
                            {uploadingPortrait ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                          </Button>
                        </div>
                        
                        {/* AI Edit Prompt */}
                        <div className="flex items-center gap-1.5">
                          <Input
                            value={portraitEditPrompt}
                            onChange={(e) => setPortraitEditPrompt(e.target.value)}
                            placeholder="Describe edits... (e.g. add a scar)"
                            className="flex-1 bg-slate-950/50 border-white/10 text-slate-200 text-xs h-7"
                            onKeyDown={(e) => e.key === 'Enter' && handleEditPortrait()}
                            data-testid="portrait-edit-input"
                          />
                          <Button
                            size="sm"
                            onClick={handleEditPortrait}
                            disabled={editingPortrait || !portraitEditPrompt.trim()}
                            className="bg-purple-600/80 hover:bg-purple-500 text-white text-xs h-7 px-2"
                            data-testid="edit-portrait-btn"
                          >
                            {editingPortrait ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Edit'}
                          </Button>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <div className="glass-card p-2 rounded border border-white/5"><span className="text-slate-500">Race:</span> <span className="text-slate-200">{character.race}</span></div>
                        <div className="glass-card p-2 rounded border border-white/5"><span className="text-slate-500">Class:</span> <span className="text-slate-200">{character.char_class}</span></div>
                      </div>
                      {character.stats && Object.entries(character.stats).filter(([k]) => !['hp','max_hp','armor_class','level','eac','kac','damage_resistance','total_points'].includes(k)).map(([key, val]) => (
                        <div key={key} className="flex justify-between py-1 border-b border-white/5">
                          <span className="text-slate-400 capitalize">{key.replace(/_/g,' ')}</span>
                          <span className="text-slate-200 font-medium">{typeof val === 'object' ? JSON.stringify(val) : String(val)}</span>
                        </div>
                      ))}
                      {character.backstory && (
                        <div className="mt-2">
                          <span className="text-slate-500 block mb-1">Backstory</span>
                          <p className="text-slate-300 text-xs leading-relaxed">{character.backstory}</p>
                        </div>
                      )}
                    </div>
                  </TabsContent>

                  {/* NPCs (left sidebar) */}
                  <TabsContent value="npcs" className="flex-1 min-h-0 overflow-y-auto p-4 relative">
                    <NPCListTab npcs={npcs} onDelete={async (id) => { await axios.delete(`${API}/npcs/${id}`, { withCredentials: true }); fetchNpcs(); }} onToggleActive={toggleNpcActive} onNpcUpdated={fetchNpcs} campaignId={campaignId} />
                  </TabsContent>

                  {/* Inventory (left sidebar) */}
                  <TabsContent value="inventory" className="flex-1 min-h-0 overflow-y-auto p-4">
                    <InventoryTab character={character} characterId={characterId} onUpdated={fetchCharacter} />
                  </TabsContent>
                </Tabs>
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* Main Chat Area */}
        {hasMap && showMap && chatMinimized ? (
          /* Minimized chat bar */
          <div className="pointer-events-auto fixed bottom-10 left-1/2 -translate-x-1/2 z-30">
            <button
              onClick={() => setChatMinimized(false)}
              className="bg-slate-950/90 backdrop-blur-xl border border-blue-500/20 rounded-lg px-6 py-2 flex items-center gap-3 hover:border-blue-400/40 transition-all group"
              data-testid="chat-expand-btn"
            >
              <div className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
              <span className="text-xs font-mono text-blue-400/80 group-hover:text-blue-300">Open Comms</span>
              {messages.length > 0 && (
                <span className="text-[10px] font-mono text-slate-500">{messages.length} messages</span>
              )}
            </button>
          </div>
        ) : (
        <div className={`flex flex-col overflow-hidden pointer-events-auto relative ${hasMap && showMap ? 'bg-slate-950/80 backdrop-blur-md w-[600px] mx-auto rounded-lg border border-white/5 my-1' : 'flex-1'}`}>
          {/* Minimize button for Star Wars map mode */}
          {hasMap && showMap && (
            <button
              onClick={() => setChatMinimized(true)}
              className="absolute top-2 right-2 z-10 p-1 rounded text-slate-500 hover:text-blue-400 hover:bg-blue-500/10 transition-all"
              title="Minimize chat"
              data-testid="chat-minimize-btn"
            >
              <Minimize2 className="h-3.5 w-3.5" />
            </button>
          )}
          {/* Messages */}
          <div
            ref={chatScrollRef}
            onScroll={(e) => {
              const el = e.currentTarget;
              const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
              setShowScrollBottom(dist > 200);
            }}
            className="flex-1 overflow-y-auto px-3 sm:px-6 py-4 sm:py-8"
            data-testid="chat-container"
          >
            <div className="max-w-4xl mx-auto space-y-4 sm:space-y-6">
              {/* Previously On... banner for chapter carry-over */}
              {currentCampaign?.previously_on && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="glass-card p-4 rounded-lg border border-purple-500/20 mb-4 group relative"
                  data-testid="previously-on-banner"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <p className="text-purple-400 font-mono font-bold text-sm mb-2">Previously on... (Chapter {(currentCampaign.chapter_number || 1) - 1})</p>
                      <p className="text-slate-300 text-sm leading-relaxed italic whitespace-pre-wrap">{currentCampaign.previously_on}</p>
                    </div>
                    <button
                      onClick={deletePreviouslyOn}
                      className="p-1.5 rounded text-slate-600 hover:text-red-400 hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition-all flex-shrink-0"
                      title="Delete Previously On — removes stale context from AI"
                      data-testid="delete-previously-on"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </motion.div>
              )}
              
              {messages.length === 0 ? (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-center py-12"
                >
                  <h2 className="text-3xl font-bold text-slate-300 mb-4 font-mono">
                    Your Adventure Begins...
                  </h2>
                  <p className="text-slate-500 text-lg">
                    {character.starting_location || 'Type a message to start your epic quest'}
                  </p>
                </motion.div>
              ) : (
                messages.map((msg, index) => (
                  <motion.div
                    key={msg.message_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} group`}
                  >
                    {/* Player avatar on user messages */}
                    {msg.role === 'user' && character?.portrait_url && (
                      <div className="flex-shrink-0 mr-2 self-end mb-1 order-2 ml-2">
                        <div className="w-8 h-8 rounded-full overflow-hidden border border-blue-400/30 shadow-lg shadow-blue-500/10" data-testid="player-avatar">
                          <img src={character.portrait_url} alt="" className="w-full h-full object-cover" />
                        </div>
                      </div>
                    )}
                    {/* Summary message — legacy support for old data */}
                    {msg.role === 'summary' ? (
                      <div className="w-full my-2 glass-card rounded-lg border border-amber-500/20 bg-amber-500/5 p-4">
                        <p className="whitespace-pre-wrap leading-relaxed text-slate-300 text-sm">{msg.content}</p>
                      </div>
                    ) : (
                    <div
                      className={`max-w-[80%] rounded-2xl p-4 ${
                        msg.role === 'user'
                          ? 'bg-blue-400/10 border border-blue-400/20 text-slate-200 rounded-br-none order-1'
                          : 'glass-card text-slate-300 rounded-bl-none'
                      }`}
                    >
                      {/* Legacy NPC name header for old messages */}
                      {msg.role === 'npc' && msg.npc_name && (
                        <div className="flex items-center gap-2 mb-2 pb-2 border-b border-amber-500/15">
                          <Users className="h-3.5 w-3.5 text-amber-400" />
                          <span className="text-amber-400 font-mono font-bold text-sm">{msg.npc_name}</span>
                        </div>
                      )}
                      <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                      <div className="flex items-center justify-between mt-2">
                        <p className="text-xs text-slate-500">
                          {new Date(msg.timestamp).toLocaleTimeString()}
                        </p>
                        <div className="flex items-center gap-1">
                          {!msg.message_id.startsWith('temp_') && (
                            <>
                              <button
                                onClick={() => anchorHere(msg.message_id)}
                                className="text-xs flex items-center gap-1 px-2 py-0.5 rounded text-slate-600 hover:text-blue-400 hover:bg-blue-500/10 opacity-0 group-hover:opacity-100 transition-all"
                                data-testid={`anchor-msg-${msg.message_id}`}
                                title="Start from here — delete all messages before this point"
                              >
                                <Anchor className="h-3 w-3" />
                              </button>
                              <button
                                onClick={() => deleteMessage(msg.message_id)}
                                className="text-xs flex items-center gap-1 px-2 py-0.5 rounded text-slate-600 hover:text-red-400 hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition-all"
                                data-testid={`delete-msg-${msg.message_id}`}
                                title="Delete message"
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </>
                          )}
                          {(msg.role === 'assistant' || msg.role === 'npc') && !msg.message_id.startsWith('temp_') && (
                            <button
                              onClick={() => rememberMessage(msg.message_id)}
                              className={`text-xs flex items-center gap-1 px-2 py-0.5 rounded transition-all ${
                                memories.some(m => m.message_id === msg.message_id)
                                  ? 'text-amber-400 bg-amber-500/15'
                                  : 'text-slate-600 hover:text-amber-400 opacity-0 group-hover:opacity-100'
                              }`}
                              data-testid={`remember-msg-${msg.message_id}`}
                              title="Remember this message"
                            >
                              <Bookmark className="h-3 w-3" />
                              {memories.some(m => m.message_id === msg.message_id) ? 'Remembered' : 'Remember'}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    )}
                  </motion.div>
                ))
              )}
              
              {loading && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-start">
                  <div className="glass-card rounded-2xl p-4 rounded-bl-none">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                      <span className="text-xs text-slate-500 font-mono ml-2">
                        {availableModels.standard_models?.concat(availableModels.premium_models || [])?.find(m => m.id === selectedModel)?.label || selectedModel}
                      </span>
                    </div>
                  </div>
                </motion.div>
              )}
              
              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Scroll to bottom floating button */}
          {showScrollBottom && (
            <button
              onClick={() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })}
              className="absolute bottom-24 right-4 sm:right-6 z-20 w-11 h-11 rounded-full bg-blue-500/90 hover:bg-blue-500 text-white shadow-lg shadow-blue-500/30 backdrop-blur flex items-center justify-center transition-all border border-blue-300/30"
              data-testid="scroll-to-bottom-btn"
              title="Scroll to latest"
            >
              <ChevronDown className="h-5 w-5" />
            </button>
          )}

          {/* Input Area */}
          <div className="glass-card border-t border-white/5 px-3 sm:px-6 py-3 sm:py-4 flex-shrink-0">
            <div className="max-w-4xl mx-auto">
              {/* Character Limit Warning */}
              {characterUsage.percentage >= 90 && (
                <div className={`mb-2 px-3 py-2 rounded-lg text-xs font-mono border ${
                  characterUsage.percentage >= 100 
                    ? 'bg-red-900/40 border-red-500/50 text-red-300' 
                    : 'bg-yellow-900/30 border-yellow-500/30 text-yellow-300'
                }`} data-testid="char-limit-warning">
                  {characterUsage.percentage >= 100 
                    ? `Character limit reached (${characterUsage.count.toLocaleString()}/${characterUsage.limit.toLocaleString()}). Open the Living Memory tab and click "Compress Now" to continue playing.`
                    : `Approaching character limit (${characterUsage.percentage}%). Consider compressing your story soon.`
                  }
                </div>
              )}
              {/* Dice Roller */}
              <DiceRoller
                show={showDiceRoller}
                diceType={diceType}
                setDiceType={setDiceType}
                diceCount={diceCount}
                setDiceCount={setDiceCount}
                diceModifier={diceModifier}
                setDiceModifier={setDiceModifier}
                onRollAndSend={(e) => handleSendMessage(e, true)}
                disabled={false}
              />

              {/* Model picker + Dice toggle row */}
              <div className="flex items-center gap-2 mb-2">
                <Button
                  type="button"
                  onClick={() => { setShowDiceRoller(!showDiceRoller); }}
                  variant="outline"
                  className="border-blue-400/30 text-blue-400 hover:bg-blue-400/10 shrink-0"
                  data-testid="dice-roller-toggle"
                >
                  <Dices className="h-5 w-5" />
                </Button>

                <select
                  value={selectedModel}
                  onChange={(e) => setSelectedModel(e.target.value)}
                  className="bg-slate-900/80 border border-slate-600/40 text-slate-200 text-xs font-mono rounded-lg px-2 py-2 appearance-none cursor-pointer hover:border-blue-400/50 transition-colors focus:outline-none focus:border-blue-400"
                  data-testid="model-picker"
                  style={{minWidth: '110px'}}
                >
                  <optgroup label="Standard">
                    {availableModels.standard_models?.map(m => (
                      <option key={m.id} value={m.id}>{m.label}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Premium">
                    {availableModels.premium_models?.map(m => (
                      <option key={m.id} value={m.id}>{m.label}</option>
                    ))}
                  </optgroup>
                </select>
              </div>

              {/* Chat input + Send */}
              <div className="flex gap-2">
                <textarea
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  placeholder={t('game.your_message')}
                  className="flex-1 bg-slate-950/50 border border-white/10 focus:border-blue-400/50 text-slate-200 rounded-md px-3 py-2 text-sm resize-none min-h-[40px] max-h-[200px]"
                  disabled={loading}
                  rows={2}
                  data-testid="message-input"
                  onInput={(e) => {
                    e.target.style.height = 'auto';
                    e.target.style.height = Math.min(e.target.scrollHeight, 200) + 'px';
                  }}
                />
                
                <Button
                  type="button"
                  onClick={(e) => handleSendMessage(e)}
                  disabled={loading || !inputMessage.trim() || characterUsage.percentage >= 100}
                  className="bg-blue-400 hover:bg-blue-300 text-black font-bold px-6 self-end shrink-0"
                  data-testid="send-message-btn"
                >
                  <Send className="h-5 w-5" />
                </Button>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Toggle Right Sidebar */}
        <button
          onClick={() => setRightSidebarOpen(!rightSidebarOpen)}
          className="w-12 md:w-8 lg:w-6 bg-slate-900/70 hover:bg-slate-900 border-l border-white/10 flex items-center justify-center transition-colors flex-shrink-0 pointer-events-auto"
          data-testid="toggle-right-sidebar"
        >
          {rightSidebarOpen ? <ChevronRight className="h-5 w-5 md:h-4 md:w-4 text-slate-300" /> : <ChevronLeft className="h-5 w-5 md:h-4 md:w-4 text-slate-300" />}
        </button>

        {/* Right Sidebar - Story / Rules / Lore / Notes */}
        <AnimatePresence>
          {rightSidebarOpen && (
            <>
              {/* Mobile overlay backdrop */}
              <div className="fixed inset-0 bg-black/60 z-[55] md:hidden" onClick={() => setRightSidebarOpen(false)} />
              <motion.div
                initial={{ x: '100%', opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: '100%', opacity: 0 }}
                transition={{ type: 'tween', duration: 0.2 }}
                className={`pointer-events-auto border-l border-white/5 overflow-hidden flex flex-col h-[100dvh] md:h-full fixed md:relative right-0 top-0 z-[60] md:z-auto w-full md:w-[350px] ${hasMap && showMap ? 'bg-slate-950 md:bg-slate-950/90 md:backdrop-blur-xl' : 'bg-slate-950 md:bg-transparent glass-card'}`}
                data-testid="info-sidebar"
              >
              <Tabs defaultValue="lore" className="h-full flex flex-col">
                <div className="flex items-center bg-slate-950 md:bg-slate-900/50 flex-shrink-0 sticky top-0 z-20 pt-[env(safe-area-inset-top)] border-b border-white/5 md:border-b-0">
                  {/* Mobile close button */}
                  <button
                    onClick={() => setRightSidebarOpen(false)}
                    className="md:hidden flex items-center justify-center w-12 h-12 text-slate-300 hover:text-white hover:bg-slate-700/50 transition-colors flex-shrink-0"
                    data-testid="sidebar-close-btn"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                  <TabsList className="grid flex-1 grid-cols-5 bg-transparent">
                  <TabsTrigger value="lore" className="data-[state=active]:bg-emerald-400/20 text-xs" data-testid="lore-tab">
                    <Library className="h-4 w-4 mr-1" /><span className="hidden sm:inline">Lore</span>
                  </TabsTrigger>
                  <TabsTrigger value="arc" className="data-[state=active]:bg-fuchsia-400/20 text-xs" data-testid="arc-tab">
                    <Wand2 className="h-4 w-4 mr-1" /><span className="hidden sm:inline">Arc</span>
                  </TabsTrigger>
                  <TabsTrigger value="rules" className="data-[state=active]:bg-purple-400/20 text-xs" data-testid="rules-tab">
                    <ScrollText className="h-4 w-4 mr-1" /><span className="hidden sm:inline">Rules</span>
                  </TabsTrigger>
                  <TabsTrigger value="memories" className="data-[state=active]:bg-amber-400/20 text-xs" data-testid="memories-tab">
                    <Brain className="h-4 w-4 mr-1" /><span className="hidden sm:inline">Notes</span>
                  </TabsTrigger>
                  <TabsTrigger value="history" className="data-[state=active]:bg-blue-400/20 text-xs">
                    <BookOpen className="h-4 w-4 mr-1" /><span className="hidden sm:inline">Story</span>
                  </TabsTrigger>
                </TabsList>
                </div>

                {/* Rules */}
                <div className="flex-shrink-0 px-4 py-2 border-b border-white/5 bg-slate-950/80">
                  <div className="flex items-center justify-between text-xs font-mono">
                    <span className="text-blue-400 font-bold truncate">{character.name}</span>
                    <div className="flex gap-3 text-slate-400">
                      <span>HP <span className={character.stats.hp <= (character.stats.max_hp || 10) / 4 ? 'text-red-400' : 'text-blue-300'}>{character.stats.hp}/{character.stats.max_hp}</span></span>
                      {character.ruleset === 'gurps' ? (
                        <>
                          <span>DR <span className="text-blue-300">{character.stats.damage_resistance || 0}</span></span>
                          <span><span className="text-amber-400">{character.stats.total_points || 100} CP</span></span>
                        </>
                      ) : character.ruleset === 'starfinder' ? (
                        <>
                          <span>EAC <span className="text-blue-300">{character.stats.eac || 10}</span></span>
                          <span>Lv <span className="text-blue-300">{character.stats.level}</span></span>
                        </>
                      ) : (
                        <>
                          <span>AC <span className="text-blue-300">{character.stats.armor_class}</span></span>
                          <span>Lv <span className="text-blue-300">{character.stats.level}</span></span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Lore / Story Bible */}
                <TabsContent value="lore" className="flex-1 min-h-0 overflow-y-auto p-4">
                  <StoryBibleTab campaignId={campaignId} messageCount={messages.length} />
                </TabsContent>

                {/* Arc / Forward Planning */}
                <TabsContent value="arc" className="flex-1 min-h-0 overflow-y-auto p-4">
                  <StoryArcTab campaignId={campaignId} />
                </TabsContent>

                {/* Rules */}
                <TabsContent value="rules" className="flex-1 min-h-0 overflow-y-auto p-4">
                  <div className="space-y-4">
                    <h3 className="text-lg font-bold text-purple-400 font-mono">GM Rules</h3>
                    <div className="glass-card rounded-lg p-3 border border-purple-500/15 text-xs text-slate-400 space-y-2" data-testid="rules-instructions">
                      <p className="text-purple-300 font-mono font-bold text-sm">How to write rules the AI will follow:</p>
                      <ul className="list-disc list-inside space-y-1 text-slate-400">
                        <li><strong className="text-slate-300">Be direct:</strong> "Never kill the player character without warning"</li>
                        <li><strong className="text-slate-300">Use imperatives:</strong> "Always describe the weather when entering a new area"</li>
                        <li><strong className="text-slate-300">Set boundaries:</strong> "Do not introduce technology in this medieval setting"</li>
                        <li><strong className="text-slate-300">Define tone:</strong> "Keep dialogue witty and sarcastic"</li>
                      </ul>
                    </div>
                    <textarea
                      value={gmRules}
                      onChange={(e) => setGmRules(e.target.value)}
                      placeholder={"Never kill the player without a fair chance to escape.\nAlways describe NPC emotions and body language.\nKeep combat encounters challenging but survivable."}
                      className="w-full h-48 bg-slate-950/50 border border-white/10 rounded-lg p-3 text-sm text-slate-200 placeholder-slate-600 resize-none focus:border-purple-400/40 focus:outline-none"
                      data-testid="gm-rules-textarea"
                    />
                    <Button
                      onClick={saveGmRules}
                      disabled={savingRules}
                      className="w-full bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 border border-purple-400/30"
                      data-testid="save-rules-btn"
                    >
                      {savingRules ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ScrollText className="h-4 w-4 mr-2" />}
                      {savingRules ? 'Saving...' : 'Save Rules'}
                    </Button>
                  </div>
                </TabsContent>

                {/* Memories */}
                <TabsContent value="memories" className="flex-1 min-h-0 overflow-y-auto p-4">
                  <div className="space-y-4">
                    <h3 className="text-lg font-bold text-amber-400 font-mono">Story Notes</h3>
                    <p className="text-slate-500 text-xs">Write anything you want the AI to always remember. This stays in context permanently.</p>
                    <textarea
                      value={storyNotes}
                      onChange={(e) => setStoryNotes(e.target.value)}
                      placeholder={"My character secretly works for the Empire.\nThe bartender on Tatooine owes me a favor.\nCurrently heading to Coruscant.\nDon't forget: I have a bounty on my head."}
                      className="w-full h-36 bg-slate-950/50 border border-white/10 rounded-lg p-3 text-sm text-slate-200 placeholder-slate-600 resize-none focus:border-amber-400/40 focus:outline-none"
                      data-testid="story-notes-textarea"
                    />
                    <Button
                      onClick={saveStoryNotes}
                      disabled={savingNotes}
                      className="w-full bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 border border-amber-400/30"
                      data-testid="save-notes-btn"
                    >
                      {savingNotes ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Brain className="h-4 w-4 mr-2" />}
                      {savingNotes ? 'Saving...' : 'Save Notes'}
                    </Button>

                    <h3 className="text-lg font-bold text-amber-400 font-mono pt-4 border-t border-white/5">Bookmarked Memories</h3>
                    <p className="text-slate-500 text-xs">Specific moments you bookmarked. These are always in the AI's context.</p>
                    {memories.length > 0 ? (
                      <div className="space-y-2">
                        {memories.map((mem) => (
                          <div key={mem.memory_id} className="glass-card p-3 rounded-lg border border-amber-500/10 group">
                            <p className="text-slate-200 text-sm leading-relaxed">{mem.summary}</p>
                            <div className="flex items-center justify-between mt-2">
                              <span className="text-xs text-slate-600">{new Date(mem.created_at).toLocaleDateString()}</span>
                              <button
                                onClick={() => deleteMemory(mem.memory_id)}
                                className="text-red-400/50 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                                data-testid={`delete-memory-${mem.memory_id}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-slate-500 text-sm text-center py-4">No memories saved yet. Click the bookmark icon on important AI messages.</p>
                    )}

                    <h3 className="text-lg font-bold text-emerald-400 font-mono pt-4 border-t border-white/5">Auto-Tracked Facts</h3>
                    <p className="text-slate-500 text-xs">The AI automatically extracts and remembers key details from your campaign. These are always in context.</p>
                    {campaignFacts ? (
                      <div className="glass-card p-3 rounded-lg border border-emerald-500/10">
                        <pre className="text-slate-300 text-xs leading-relaxed whitespace-pre-wrap font-mono">{campaignFacts}</pre>
                        <span className="text-xs text-slate-600 block mt-2">Auto-updated after each exchange</span>
                      </div>
                    ) : (
                      <p className="text-slate-500 text-sm text-center py-4">Facts will appear here after your first conversation exchange.</p>
                    )}
                  </div>
                </TabsContent>

                {/* Story — Living Compressed Memory */}
                <TabsContent value="history" className="flex-1 min-h-0 overflow-y-auto p-4">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-bold text-blue-400 font-mono">Living Memory</h3>
                      <div className="flex items-center gap-2">
                        {summaries.length > 0 && (
                          <Button
                            onClick={deleteSummaries}
                            size="sm"
                            className="bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20"
                            data-testid="delete-summaries-btn"
                          >
                            <Trash2 className="h-3.5 w-3.5 mr-1" />
                            Clear
                          </Button>
                        )}
                        <Button
                          onClick={compressSummaries}
                          disabled={compressing}
                          size="sm"
                          className={confirmCompress
                            ? 'bg-amber-500/30 hover:bg-amber-500/40 text-amber-200 border border-amber-400/50'
                            : 'bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 border border-purple-500/30'}
                          data-testid="compress-btn"
                        >
                          {compressing ? 'Compressing...' : confirmCompress ? 'Confirm Compress' : 'Compress Now'}
                        </Button>
                      </div>
                    </div>
                    <p className="text-slate-500 text-xs">
                      Your campaign's living memory. Each compression folds new events into this evolving narrative (40k cap). The AI uses this to remember your entire story. You can edit it directly.
                    </p>
                    {summaries.length > 0 ? (
                      <>
                        <div className="text-[10px] font-mono text-slate-600 text-right">
                          {editableSummary.length.toLocaleString()} / 40,000 chars
                        </div>
                        <textarea
                          value={editableSummary}
                          onChange={(e) => setEditableSummary(e.target.value)}
                          className="w-full h-64 bg-slate-950/50 border border-white/10 rounded-lg p-3 text-sm text-slate-200 placeholder-slate-600 resize-y focus:border-blue-400/40 focus:outline-none"
                          data-testid="campaign-summary-textarea"
                        />
                        <Button
                          onClick={saveCampaignSummary}
                          disabled={savingSummary}
                          className="w-full bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 border border-blue-400/30"
                          data-testid="save-summary-btn"
                        >
                          {savingSummary ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <BookOpen className="h-4 w-4 mr-2" />}
                          {savingSummary ? 'Saving...' : 'Save Memory'}
                        </Button>
                      </>
                    ) : (
                      <div className="text-center py-8">
                        <p className="text-slate-500 text-sm">No compressed memory yet.</p>
                        <p className="text-slate-600 text-xs mt-1">Click "Compress Now" when you approach the character limit. Old messages will be folded into a living narrative the AI uses to remember your story.</p>
                      </div>
                    )}

                    {/* ── Optional V2 Structured View (non-destructive) ── */}
                    <div className="mt-6 border-t border-white/10 pt-4 space-y-2">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-xs font-mono uppercase tracking-wider text-emerald-300">V2 Structured View</span>
                          <span className="text-[10px] text-slate-500">(optional, supplements V1)</span>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Button
                            onClick={buildV2FromV1}
                            disabled={buildingV2}
                            size="sm"
                            className="bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 border border-emerald-400/30 text-xs h-7"
                            data-testid="build-v2-btn"
                          >
                            {buildingV2
                              ? <><Loader2 className="h-3 w-3 animate-spin mr-1" /> Building…</>
                              : (v2EditState && (v2EditState.entities?.length || v2EditState.event_log?.length))
                                ? 'Refresh V2'
                                : 'Build V2 from V1'}
                          </Button>
                          {(v2EditState && (v2EditState.entities?.length || v2EditState.event_log?.length || v2EditState.narrative_summary)) ? (
                            <Button
                              onClick={() => setShowV2Panel(s => !s)}
                              size="sm"
                              variant="ghost"
                              className="text-emerald-300 hover:bg-emerald-500/10 text-xs h-7 px-2"
                              data-testid="toggle-v2-panel-btn"
                            >
                              {showV2Panel ? 'Hide' : 'Show'} V2
                            </Button>
                          ) : null}
                        </div>
                      </div>
                      <p className="text-[10px] text-slate-500">
                        Non-destructive. Reads your V1 living memory + recent chat and produces a structured JSON snapshot (entities, threads, events, player state). Safe to run anytime — V1 is never touched.
                      </p>

                      {showV2Panel && v2EditState && (
                        <div className="space-y-3 pt-2" data-testid="v2-memory-panel">
                          {/* V2 Narrative */}
                          <div className="glass-card rounded-lg border border-blue-500/15">
                            <div className="p-3 border-b border-white/5 flex items-center justify-between">
                              <h4 className="text-xs font-bold text-blue-300 font-mono">V2 Narrative Summary</h4>
                              <span className="text-[10px] font-mono text-slate-600">
                                {(v2EditState.narrative_summary || '').length.toLocaleString()} / 30,000
                              </span>
                            </div>
                            <div className="p-3 space-y-2">
                              <textarea
                                value={v2EditState.narrative_summary || ''}
                                onChange={(e) => setV2EditState({ ...v2EditState, narrative_summary: e.target.value })}
                                className="w-full h-40 bg-slate-950/50 border border-white/10 rounded p-2 text-xs text-slate-200 resize-y focus:border-blue-400/40 focus:outline-none"
                                data-testid="v2-narrative-textarea"
                              />
                              <Button
                                onClick={() => saveV2Section('narrative_summary')}
                                disabled={savingV2Section === 'narrative_summary'}
                                size="sm"
                                className="w-full bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 border border-blue-400/30 text-xs h-7"
                                data-testid="save-v2-narrative-btn"
                              >
                                {savingV2Section === 'narrative_summary' ? 'Saving…' : 'Save Narrative'}
                              </Button>
                            </div>
                          </div>

                          {/* Entities */}
                          <div className="glass-card rounded-lg border border-purple-500/15">
                            <button
                              onClick={() => setExpandedV2({ ...expandedV2, entities: !expandedV2.entities })}
                              className="w-full p-3 border-b border-white/5 flex items-center justify-between hover:bg-purple-500/5"
                            >
                              <h4 className="text-xs font-bold text-purple-300 font-mono">
                                Entities ({(v2EditState.entities || []).length})
                              </h4>
                              {expandedV2.entities ? <ChevronUp className="h-4 w-4 text-purple-300" /> : <ChevronDown className="h-4 w-4 text-purple-300" />}
                            </button>
                            {expandedV2.entities && (
                              <div className="p-3 space-y-2 max-h-[400px] overflow-y-auto">
                                {(v2EditState.entities || []).map((ent, idx) => (
                                  <details key={ent.entity_id || idx} className="bg-slate-950/40 rounded border border-white/5 px-2 py-1">
                                    <summary className="cursor-pointer text-xs py-1 flex items-center gap-2">
                                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-mono ${
                                        ent.importance_tier === 'primary' ? 'bg-amber-500/20 text-amber-300' :
                                        ent.importance_tier === 'secondary' ? 'bg-blue-500/20 text-blue-300' :
                                        'bg-slate-700/50 text-slate-400'
                                      }`}>{(ent.importance_tier || '?').toUpperCase()}</span>
                                      <span className="text-slate-500 text-[10px] font-mono">{ent.entity_type}</span>
                                      <span className="text-slate-100 font-semibold">{ent.name || ent.entity_id}</span>
                                      <span className="text-slate-500 text-[10px] truncate flex-1">{ent.current_status?.slice(0, 80)}</span>
                                    </summary>
                                    <div className="pt-2 pb-1 text-[11px] space-y-1.5">
                                      <textarea
                                        value={JSON.stringify(ent, null, 2)}
                                        onChange={(e) => {
                                          try {
                                            const updated = JSON.parse(e.target.value);
                                            const list = [...v2EditState.entities];
                                            list[idx] = updated;
                                            setV2EditState({ ...v2EditState, entities: list });
                                          } catch {}
                                        }}
                                        rows={Math.min(20, (JSON.stringify(ent, null, 2).split('\n').length || 5))}
                                        className="w-full bg-slate-950/70 border border-white/10 rounded p-2 text-[11px] font-mono text-slate-200 focus:border-purple-400/40 focus:outline-none"
                                      />
                                      <button
                                        onClick={() => {
                                          const list = v2EditState.entities.filter((_, i) => i !== idx);
                                          setV2EditState({ ...v2EditState, entities: list });
                                        }}
                                        className="text-red-400/60 hover:text-red-400 text-[10px]"
                                      >
                                        Remove this entity
                                      </button>
                                    </div>
                                  </details>
                                ))}
                                <Button
                                  onClick={() => saveV2Section('entities')}
                                  disabled={savingV2Section === 'entities'}
                                  size="sm"
                                  className="w-full bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 border border-purple-400/30 text-xs h-7"
                                >
                                  {savingV2Section === 'entities' ? 'Saving…' : 'Save Entities'}
                                </Button>
                              </div>
                            )}
                          </div>

                          {/* Open Threads */}
                          <div className="glass-card rounded-lg border border-emerald-500/15">
                            <button
                              onClick={() => setExpandedV2({ ...expandedV2, threads: !expandedV2.threads })}
                              className="w-full p-3 border-b border-white/5 flex items-center justify-between hover:bg-emerald-500/5"
                            >
                              <h4 className="text-xs font-bold text-emerald-300 font-mono">
                                Open Threads ({(v2EditState.open_threads || []).length})
                              </h4>
                              {expandedV2.threads ? <ChevronUp className="h-4 w-4 text-emerald-300" /> : <ChevronDown className="h-4 w-4 text-emerald-300" />}
                            </button>
                            {expandedV2.threads && (
                              <div className="p-3 space-y-2">
                                {(v2EditState.open_threads || []).map((thr, idx) => (
                                  <details key={thr.thread_id || idx} className="bg-slate-950/40 rounded border border-white/5 px-2 py-1">
                                    <summary className="cursor-pointer text-xs py-1 flex items-center gap-2">
                                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-mono ${
                                        thr.status === 'active' ? 'bg-emerald-500/20 text-emerald-300' :
                                        thr.status === 'approaching_resolution' ? 'bg-amber-500/20 text-amber-300' :
                                        'bg-slate-700/50 text-slate-400'
                                      }`}>{(thr.status || '?').toUpperCase()}</span>
                                      <span className="text-slate-100 truncate flex-1">{thr.description?.slice(0, 100)}</span>
                                    </summary>
                                    <div className="pt-2 pb-1">
                                      <textarea
                                        value={JSON.stringify(thr, null, 2)}
                                        onChange={(e) => {
                                          try {
                                            const updated = JSON.parse(e.target.value);
                                            const list = [...v2EditState.open_threads];
                                            list[idx] = updated;
                                            setV2EditState({ ...v2EditState, open_threads: list });
                                          } catch {}
                                        }}
                                        rows={8}
                                        className="w-full bg-slate-950/70 border border-white/10 rounded p-2 text-[11px] font-mono text-slate-200 focus:border-emerald-400/40 focus:outline-none"
                                      />
                                    </div>
                                  </details>
                                ))}
                                <Button
                                  onClick={() => saveV2Section('open_threads')}
                                  disabled={savingV2Section === 'open_threads'}
                                  size="sm"
                                  className="w-full bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 border border-emerald-400/30 text-xs h-7"
                                >
                                  {savingV2Section === 'open_threads' ? 'Saving…' : 'Save Threads'}
                                </Button>
                              </div>
                            )}
                          </div>

                          {/* Player State */}
                          <div className="glass-card rounded-lg border border-amber-500/15">
                            <button
                              onClick={() => setExpandedV2({ ...expandedV2, player: !expandedV2.player })}
                              className="w-full p-3 border-b border-white/5 flex items-center justify-between hover:bg-amber-500/5"
                            >
                              <h4 className="text-xs font-bold text-amber-300 font-mono">Player State</h4>
                              {expandedV2.player ? <ChevronUp className="h-4 w-4 text-amber-300" /> : <ChevronDown className="h-4 w-4 text-amber-300" />}
                            </button>
                            {expandedV2.player && (
                              <div className="p-3 space-y-2">
                                <textarea
                                  value={JSON.stringify(v2EditState.player_state || {}, null, 2)}
                                  onChange={(e) => {
                                    try { setV2EditState({ ...v2EditState, player_state: JSON.parse(e.target.value) }); } catch {}
                                  }}
                                  rows={10}
                                  className="w-full bg-slate-950/70 border border-white/10 rounded p-2 text-[11px] font-mono text-slate-200 focus:border-amber-400/40 focus:outline-none"
                                />
                                <Button
                                  onClick={() => saveV2Section('player_state')}
                                  disabled={savingV2Section === 'player_state'}
                                  size="sm"
                                  className="w-full bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 border border-amber-400/30 text-xs h-7"
                                >
                                  {savingV2Section === 'player_state' ? 'Saving…' : 'Save Player State'}
                                </Button>
                              </div>
                            )}
                          </div>

                          {/* Event Log */}
                          <div className="glass-card rounded-lg border border-slate-500/15">
                            <button
                              onClick={() => setExpandedV2({ ...expandedV2, events: !expandedV2.events })}
                              className="w-full p-3 border-b border-white/5 flex items-center justify-between hover:bg-slate-500/5"
                            >
                              <h4 className="text-xs font-bold text-slate-300 font-mono">
                                Event Log ({(v2EditState.event_log || []).length})
                              </h4>
                              {expandedV2.events ? <ChevronUp className="h-4 w-4 text-slate-300" /> : <ChevronDown className="h-4 w-4 text-slate-300" />}
                            </button>
                            {expandedV2.events && (
                              <div className="p-3 space-y-1 max-h-[400px] overflow-y-auto">
                                {(v2EditState.event_log || []).map((ev, idx) => (
                                  <details key={ev.event_id || idx} className="bg-slate-950/40 rounded border border-white/5 px-2 py-1">
                                    <summary className="cursor-pointer text-xs py-1 flex items-center gap-2">
                                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-mono ${
                                        ev.significance_tag === 'pivotal' ? 'bg-rose-500/20 text-rose-300' :
                                        ev.significance_tag === 'significant' ? 'bg-blue-500/20 text-blue-300' :
                                        'bg-slate-700/50 text-slate-500'
                                      }`}>{(ev.significance_tag || '?').toUpperCase()}</span>
                                      <span className="text-slate-500 text-[10px] font-mono">{ev.timestamp}</span>
                                      <span className="text-slate-200 truncate flex-1">{ev.summary?.slice(0, 100)}</span>
                                    </summary>
                                    <div className="pt-2 pb-1">
                                      <pre className="bg-slate-950/70 border border-white/10 rounded p-2 text-[10px] font-mono text-slate-300 whitespace-pre-wrap">{JSON.stringify(ev, null, 2)}</pre>
                                    </div>
                                  </details>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
